import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../../lib/supabaseClient.ts';
import './SearchResults.css';

const SearchResults = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [brands, setBrands] = useState([]);
  
  // Get parameters from URL
  const searchQuery = searchParams.get('q') || '';
  const categoryIds = searchParams.get('categories')?.split(',').filter(Boolean) || [];
  const brandNames = searchParams.get('brands')?.split(',').filter(Boolean) || [];
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedSubcategories, setSelectedSubcategories] = useState([]);
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [petType, setPetType] = useState('all'); // all, cat, dog, rongeur, oiseaux
  useEffect(() => {
    fetchCategories();
    fetchBrands();
    searchProducts();
  }, [searchParams]);

  const fetchCategories = async () => {
    try {
      let query = supabase.from('categories').select('*');
      
      if (petType !== 'all') {
        query = query.eq('pet_type', petType);
      }
      
      const { data, error } = await query.order('order', { ascending: true });
      if (!error) {
        setCategories(data || []);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };
  // Fetch subcategories whenever categories or petType change
  useEffect(() => {
    fetchSubcategories();
  }, [categories, petType]);

  const fetchSubcategories = async () => {
    try {
      let query = supabase.from('subcategories').select('*');

      // Filter by selected categories if any
      if (selectedCategories.length > 0) {
        query = query.in('category_id', selectedCategories);
      }

      // Optional: also filter by petType via categories
      if (petType !== 'all') {
        const catIdsForPet = categories.filter(c => c.pet_type === petType).map(c => c.id);
        query = query.in('category_id', catIdsForPet);
      }

      const { data, error } = await query.order('order', { ascending: true });
      if (!error) {
        setSubcategories(data || []);
      }
    } catch (error) {
      console.error('Error fetching subcategories:', error);
    }
  };

  const fetchBrands = async () => {
    try {
      const { data, error } = await supabase
        .from('products')
        .select('brand')
        .not('brand', 'is', null);
      
      if (!error && data) {
        const uniqueBrands = [...new Set(data.map(p => p.brand).filter(Boolean))].sort();
        setBrands(uniqueBrands);
      }
    } catch (error) {
      console.error('Error fetching brands:', error);
    }
  };

 const searchProducts = async () => {
    setLoading(true);

    try {
      const productIds = new Set();
      const searchTerm = searchQuery.trim().toLowerCase();

      // ------------------------------
      // STEP 1: Search in products table
      // ------------------------------
      let mainQuery = supabase.from('products').select('*');

      // Search multiple fields
      if (searchTerm) {
        mainQuery = mainQuery.or(
          `name.ilike.%${searchTerm}%,` +
          `description.ilike.%${searchTerm}%,` +
          `brand.ilike.%${searchTerm}%,` +
          `weight.ilike.%${searchTerm}%,` +
          `price_range.ilike.%${searchTerm}%,` +
          `composition.ilike.%${searchTerm}%,` +
          `ingredients.ilike.%${searchTerm}%,` +
          `analysis.ilike.%${searchTerm}%,` +
          `antioxidants.ilike.%${searchTerm}%,` +
          `energy.ilike.%${searchTerm}%`
        );
      }

      // Category filter
      if (selectedCategories.length > 0) {
        const categoryNames = categories
          .filter(c => selectedCategories.includes(c.id))
          .map(c => c.name);
        if (categoryNames.length > 0) mainQuery = mainQuery.in('category', categoryNames);
      } else if (petType !== 'all') {
        const categoryNames = categories
          .filter(c => c.pet_type === petType)
          .map(c => c.name);
        if (categoryNames.length > 0) mainQuery = mainQuery.in('category', categoryNames);
      }

      // Subcategory filter
      if (selectedSubcategories.length > 0) {
        const subcategoryNames = subcategories
          .filter(s => selectedSubcategories.includes(s.id))
          .map(s => s.name);
        if (subcategoryNames.length > 0) mainQuery = mainQuery.in('subcategory', subcategoryNames);
      }

      // Brand filter
      if (selectedBrands.length > 0) mainQuery = mainQuery.in('brand', selectedBrands);

      const { data: mainProducts, error: mainError } = await mainQuery.limit(100);
      if (mainError) console.error('Error in main products search:', mainError);
      if (mainProducts) mainProducts.forEach(p => productIds.add(p.id));

      // ------------------------------
      // STEP 2: Search in product_variations table
      // ------------------------------
      if (searchTerm) {
        const { data: variations, error: varError } = await supabase
          .from('product_variations')
          .select('product_id')
          .or(
            `variation_name.ilike.%${searchTerm}%,` +
            `weight.ilike.%${searchTerm}%,` +
            `composition.ilike.%${searchTerm}%,` +
            `price.ilike.%${searchTerm}%,` +
            `ingredients.ilike.%${searchTerm}%,` +
            `analysis.ilike.%${searchTerm}%,` +
            `antioxidants.ilike.%${searchTerm}%,` +
            `energy.ilike.%${searchTerm}%`
          );

        if (varError) console.error('Error in variations search:', varError);

        if (variations && variations.length > 0) {
          variations.forEach(v => productIds.add(v.product_id));
        }
      }

      // ------------------------------
      // STEP 3: Fetch final product details
      // ------------------------------
      if (productIds.size > 0) {
        const { data: finalProducts, error: finalError } = await supabase
          .from('products')
          .select('*')
          .in('id', Array.from(productIds));

        if (finalError) console.error('Error fetching final products:', finalError);
        setFilteredProducts(finalProducts || []);
      } else {
        // fallback if no products matched variations
        setFilteredProducts(mainProducts || []);
      }

    } catch (error) {
      console.error('Error searching products:', error);
      setFilteredProducts([]);
    } finally {
      setLoading(false);
    }
  };


  const handleProductClick = (productId) => {
    navigate(`/shop-details/${productId}`);
  };

  return (
    <div className="search-results-page">
      {/* Header */}
      <div className="results-page-header">
        <button className="back-button" onClick={() => navigate(-1)}>
          ← Retour
        </button>
        <h1>Résultats de recherche</h1>
      </div>

      {/* Search Summary */}
      <div className="search-summary">
        {searchQuery && (
          <p>
            Recherche pour: <strong>"{searchQuery}"</strong>
          </p>
        )}
        {(categoryIds.length > 0 || brandNames.length > 0) && (
          <div className="active-filters">
            {categoryIds.length > 0 && (
              <span className="filter-badge">
                {categoryIds.length} catégorie(s)
              </span>
            )}
            {brandNames.length > 0 && (
              <span className="filter-badge">
                {brandNames.length} marque(s)
              </span>
            )}
          </div>
        )}
      </div>

      {/* Results */}
      <div className="results-container">
        {loading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Chargement des résultats...</p>
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="empty-state">
            <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="#ccc" strokeWidth="1">
              <circle cx="11" cy="11" r="8"></circle>
              <path d="m21 21-4.35-4.35"></path>
            </svg>
            <h2>Aucun produit trouvé</h2>
            <p>Essayez d'ajuster vos critères de recherche</p>
            <button className="button-secondary" onClick={() => navigate('/shop')}>
              Parcourir tous les produits
            </button>
          </div>
        ) : (
          <>
            <p className="results-count">
              {filteredProducts.length} produit(s) trouvé(s)
            </p>
            <div className="products-grid">
              {filteredProducts.map((product) => (
                <div
                  key={product.id}
                  className="product-card-search"
                  onClick={() => handleProductClick(product.id)}
                >
                  <div className="product-image-wrapper">
                    {product.image ? (
                      <img src={product.image} alt={product.name} />
                    ) : (
                      <div className="no-image">No image</div>
                    )}
                    {product.promo && (
                      <span className="promo-badge">
                        -{product.promo_percentage || 10}%
                      </span>
                    )}
                    {product.new && <span className="new-badge">Nouveau</span>}
                    {product.best && <span className="best-badge">⭐ Meilleur</span>}
                  </div>

                  <div className="product-info-search">
                    <p className="product-brand">{product.brand || 'Marque inconnue'}</p>
                    <h4 className="product-name">{product.name || 'Produit inconnu'}</h4>
                    <p className="product-description">
                      {product.description || 'Aucune description disponible'}
                    </p>

                    {product.stock_status && (
                      <div className="stock-status">
                        <span className={`stock-badge ${product.stock_status.toLowerCase()}`}>
                          {product.stock_status === 'in_stock' && '✓ En stock'}
                          {product.stock_status === 'out_of_stock' && '✕ Rupture de stock'}
                          {product.stock_status === 'low_stock' && '⚠ Stock limité'}
                        </span>
                      </div>
                    )}

                  <div className="product-price-section">
                        {product.promo && product.price_range ? (
                          <div className="price-with-promo">
                            <span className="old-price">
                              {new Intl.NumberFormat("fr-FR", {
                                minimumFractionDigits: 3,
                                maximumFractionDigits: 3,
                              }).format(parseFloat(product.price_range))}{" "}
                              DT
                            </span>

                            <span className="current-price">
                              {new Intl.NumberFormat("fr-FR", {
                                minimumFractionDigits: 3,
                                maximumFractionDigits: 3,
                              }).format(
                                parseFloat(product.price_range) *
                                  (1 - (product.promo_percentage || 10) / 100)
                              )}{" "}
                              DT
                            </span>
                          </div>
                        ) : product.price_range ? (
                          <span className="current-price-solo">
                            {new Intl.NumberFormat("fr-FR", {
                              minimumFractionDigits: 3,
                              maximumFractionDigits: 3,
                            }).format(parseFloat(product.price_range))}{" "}
                            DT
                          </span>
                        ) : (
                          <span className="current-price-solo">Prix indisponible</span>
                        )}
                      </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SearchResults;